import React, { MutableRefObject } from 'react';
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Card, LinearProgress, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SlantedBanner } from 'components';
import { userInfosSelector } from 'reducers/app.reducer';
import {
  cssCard,
  cssResultContainer,
  progressStyles,
  Result,
  SubGoal,
  Unit,
  UserName,
} from './styles';

const useProgressStyles = makeStyles(progressStyles);

type ChallengeRankCardTypes = {
  areResultsAnonymized: boolean;
  entity: {
    '@id'?: string;
    userId?: string;
    name?: string;
    profile: Record<'firstName' | 'lastName', string>;
  };
  goalValue?: number;
  isGroup?: boolean;
  mode: 'value' | 'percent';
  rank: number;
  resultValue?: number;
  unit?: string | JSX.Element;
};

const ChallengeRankCard = React.forwardRef(
  (
    {
      areResultsAnonymized,
      entity,
      goalValue,
      isGroup,
      mode = 'value',
      rank,
      resultValue,
      unit = '',
    }: ChallengeRankCardTypes,
    ref: ((instance: unknown) => void) | MutableRefObject<unknown> | null,
  ): JSX.Element => {
    const { t } = useTranslation();
    const { id, userGroups } = useSelector(userInfosSelector);
    const progressClasses = useProgressStyles();
    const profileId = entity.userId ?? entity['@id']!.split('/v2/users/')[1];
    const isMe = isGroup
      ? userGroups?.includes({id: Number(entity['@id']), name: entity.name!})
      : id === profileId;

    const getBannerRightText = () => {
      if (isGroup) {
        return areResultsAnonymized && !isMe
          ? t('challenges.group')
          : entity?.name!;
      }
      return areResultsAnonymized && !isMe ? (
        t('challenges.participant')
      ) : (
        <UserName>
          {entity?.profile?.firstName}{' '}
          <strong>{entity?.profile?.lastName}</strong>
        </UserName>
      );
    };

    return (
      <Card
        elevation={5}
        css={cssCard}
        id={isMe ? 'me' : ''}
        ref={isMe ? ref : undefined}
      >
        <SlantedBanner
          isHighlighted={isMe}
          leftText={
            <strong>
              {rank?.toLocaleString()}
              <sup>e</sup>
            </strong>
          }
          rightText={getBannerRightText()}
        />
        <div css={cssResultContainer}>
          {mode === 'value' && (
            <Result>
              <strong>{resultValue?.toLocaleString() || '0'}</strong>
              {goalValue !== undefined && (
                <SubGoal>/{goalValue.toLocaleString()}</SubGoal>
              )}
            </Result>
          )}
          {mode === 'percent' && (
            <Result>
              <strong>
                {(resultValue !== undefined && goalValue)
                  ? (
                      Math.round((resultValue / goalValue) * 1000) / 10
                    ).toLocaleString()
                  : '-'}
                %
              </strong>
            </Result>
          )}
          <Unit>{unit}</Unit>
        </div>
        {mode === 'percent' && (
          <LinearProgress
            variant="determinate"
            value={ (resultValue !== undefined && goalValue) ? Math.min(100, (resultValue / goalValue) * 100) : 0 }
            classes={progressClasses}
          />
        )}
      </Card>
    );
  },
);

export default ChallengeRankCard;
